import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme, type ThemePreference } from '@/lib/theme';
import { cn } from '@/lib/utils';

// Light / Dark / System segmented control.
//
// Lives in two places: the user dropdown in TopNav (where you'd expect to find
// a preference), and the sign-in page. The sign-in one matters more than it
// looks — it's the only surface an unauthenticated visitor can reach, so
// without it there is no way to change the theme before logging in, and no way
// to see that a dark theme exists at all.
//
// `System` is the default and follows the OS. It resolves through
// `lib/theme.tsx` to a `data-theme` attribute rather than the bare
// `prefers-color-scheme` variant, so a dark utility can only ever apply when
// the token block underneath it has actually been swapped.

const OPTIONS: Array<{ value: ThemePreference; label: string; Icon: typeof Sun }> = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

export function ThemeToggle({
  /** `full` fills its container (dropdown). `compact` is icon-only (page corner). */
  variant = 'full',
  className,
}: {
  variant?: 'full' | 'compact';
  className?: string;
}) {
  const { preference, setPreference } = useTheme();
  const compact = variant === 'compact';

  return (
    <div
      role="group"
      aria-label="Appearance"
      className={cn(
        'flex items-center gap-0.5',
        // Compact sits alone in a page corner with nothing around it, so it
        // needs to read as a control on its own: solid surface, full-strength
        // border, and a shadow to lift it off the background. The first pass
        // used `bg-background/80` over `bg-muted/30` — near-identical values,
        // and it disappeared.
        compact && 'rounded-lg border border-border bg-background p-1 shadow-sm',
        className,
      )}
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setPreference(value)}
            // Announces which option is selected. The visual fill alone would
            // leave a screen-reader user with three identically-named buttons.
            aria-pressed={active}
            // Icon-only in compact mode, so the name has to come from here.
            aria-label={compact ? `${label} theme` : undefined}
            title={compact ? `${label} theme` : undefined}
            className={cn(
              'flex items-center justify-center gap-1 rounded transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // 32px in compact so the corner control clears a comfortable
              // touch target; the dropdown variant is constrained by the menu.
              compact ? 'h-8 w-8' : 'h-7 flex-1 text-2xs font-medium capitalize',
              active
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {compact ? null : label}
          </button>
        );
      })}
    </div>
  );
}
